import { Box, Stack, Text, Flex, Field } from "@chakra-ui/react";
import { t } from "i18next";
import { useFormContext } from "react-hook-form";
import { ClaimsSearch } from "./ClaimsSearch";
import { InvoiceFormData } from "@/types";
import { Trans } from "react-i18next";
import { Alert, Button, ErrorMessage } from "@digital-services-gard-as/anchor-react";
import { useClaimEditor } from "./useClaimEditor.hook";
import { ClaimSection } from "./ClaimSection";
import { SubclaimSection } from "./SubclaimSection";

export const ClaimsSelect = ({ hasOcrResponse, crmId }: { hasOcrResponse: boolean; crmId?: string }) => {
    const {
        isEditing,
        canSaveEditing,
        draft,
        formValues,
        startEditing,
        cancelEditing,
        saveEditing,
        updateClaim,
        updateSubClaim
    } = useClaimEditor(crmId);

    const { claimRef, subClaimsRef, clientName, vesselName } = formValues;

    const toStrOrUndef = (val: string | null | undefined) => val ?? undefined;
    const activeClaimRef = toStrOrUndef(isEditing ? draft.claimRef : claimRef);
    const activeSubClaimRef = toStrOrUndef(isEditing ? draft.subClaimRef : subClaimsRef);
    const showSubclaimList = Boolean(activeClaimRef) && (isEditing || !subClaimsRef);

    if (!claimRef) {
        return (
            <Stack gap={4}>
                {hasOcrResponse && (
                    <Alert variant="danger">
                        <Trans i18nKey="invoices:createInvoice.generalInfo.selectClaimAlertText" />
                    </Alert>
                )}
                <ClaimsSearch onClaimChange={updateClaim} />
                <ClaimsErrorMessage />
            </Stack>
        );
    }

    return (
        <Stack gap={4}>
            <ClaimSection
                isEditing={isEditing}
                draft={draft}
                updateClaim={updateClaim}
                claimRef={claimRef}
                startEditing={startEditing}
            />
            <SubclaimSection
                activeClaimRef={activeClaimRef}
                activeSubClaimRef={activeSubClaimRef}
                showSubclaimList={showSubclaimList}
                updateSubClaim={updateSubClaim}
                subClaimsRef={subClaimsRef}
                crmId={crmId}
            />
            {isEditing && (
                <EditButtons cancelEditing={cancelEditing} saveEditing={saveEditing} disabled={!canSaveEditing} />
            )}
            <ClaimsErrorMessage />
            <Box>
                <Text mb="0.5rem" display="block" asChild>
                    <b>{t("invoices:createInvoice.generalInfo.clientName")}</b>
                </Text>

                <Flex gap="0.5rem" align="center">
                    <Text id="clientName">{clientName ?? "—"}</Text>
                </Flex>
            </Box>
            <Box>
                <Text mb="0.5rem" display="block" asChild>
                    <b>{t("invoices:createInvoice.generalInfo.vesselName")}</b>
                </Text>

                <Flex gap="0.5rem" align="center">
                    <Text id="vesselName">{vesselName ?? "—"}</Text>
                </Flex>
            </Box>
        </Stack>
    );
};

const EditButtons = ({
    cancelEditing,
    saveEditing,
    disabled
}: Readonly<{
    cancelEditing: () => void;
    saveEditing: () => void;
    disabled: boolean;
}>) => {
    return (
        <Flex gap={2} mt={4} justifyContent="flex-end">
            <Button variant="secondary" onClick={cancelEditing} data-testid="cancelEditButton">
                {t("main:cancel")}
            </Button>
            <Button onClick={saveEditing} disabled={disabled} data-testid="saveEditButton">
                {t("main:save")}
            </Button>
        </Flex>
    );
};

const ClaimsErrorMessage = () => {
    const {
        formState: { errors }
    } = useFormContext<InvoiceFormData>();
    return (
        <Field.Root invalid={Boolean(errors.claimRef || errors.subClaimsRef)}>
            <ErrorMessage size="sm" data-testid="claimReferenceError">
                {errors.claimRef?.message || errors.subClaimsRef?.message}
            </ErrorMessage>
        </Field.Root>
    );
};
